import React, { memo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import AutoComplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import _ from 'lodash';

import { Customer } from 'services/customers';
import { TextField } from 'ui/components/TextField/TextField';
import { fetchCustomerById } from 'services/customers/api';
import { activeUserHasPermission } from 'services/user/redux';
import { initialPagination } from 'ui/components/Table/ItemsTable';
import { Pagination } from 'services/search';
import { PaginationDirection } from 'services/url';

import {
  customersAutocompleteFilterOptions,
  customersAutocompleteOptionLabel,
  resolvedFetchCustomersFunction,
} from './helpers';
import { CustomersAutocompleteProps } from './types';
import NewCustomerModal from './NewCustomerModal';
import { useAsyncAutocomplete, useScrollPosition } from '../Autocomplete/hooks';

const initialCustomerPagination: Pagination = {
  ...initialPagination,
  pageSize: 50,
  sort: { sortBy: 'name', direction: PaginationDirection.Ascending },
};

const CustomersAutocomplete: React.FC<CustomersAutocompleteProps> = (props) => {
  const {
    onChange,
    dataQa,
    permissions = [],
    disabled,
    label,
    value,
    placeholder,
    error,
    required,
    disableAdd = false,
    additionalInputProps,
    expands,
    idsToRemove = [],
  } = props;

  const {
    data: customers,
    isLoading,
    innerValue,
    open,
    onCloseAutocomplete,
    onNextPage,
    onOpenAutocomplete,
    setSearchValue,
    setInnerValue,
    setData,
  } = useAsyncAutocomplete(
    value,
    resolvedFetchCustomersFunction(expands),
    fetchCustomerById,
    initialCustomerPagination
  );

  const [showModal, setShowModal] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState<string | null>(null);

  const canEdit = useSelector(activeUserHasPermission(permissions));

  const isDisabled = disabled || !canEdit;

  const filteredCustomers = idsToRemove.length
    ? customers.filter((c) => c.id && !idsToRemove.includes(c.id))
    : customers;

  const { listRef, captureScrollPosition } = useScrollPosition(customers);

  const handleAutocompleteInputChange = (
    e: any,
    val: string,
    reason: string
  ) => {
    // reason why this event is triggered
    // it can be 'input', 'clear' and 'reset'
    if (reason === 'input') {
      if (!val) {
        onOpenAutocomplete();
        setInnerValue(null);
      }
      setSearchValue(val ? encodeURIComponent(val) : null);
      onOpenAutocomplete();
      return;
    }

    if (reason === 'clear') {
      onChange(null);
    }

    setSearchValue(null);
  };

  const handleChange = (e: any, customer: Customer | null) => {
    if (customer && (customer.id === null || customer.id < 0)) {
      setNewCustomerName(customer.name);
      setShowModal(true);
      return;
    }
    setInnerValue(customer);
    onChange(customer);
  };

  const handleSaveModal = useCallback(
    async (customer: Customer) => {
      setShowModal(false);
      setData([customer]);
      setInnerValue(customer);
      onChange(customer);
    },
    [onChange, setInnerValue, setData]
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  return (
    <>
      <AutoComplete
        className="redesign"
        // TODO : RESTYLING : minor style adjustment needed for now until we use the FBOAutoSelect component
        style={{ marginTop: '0px', marginBottom: '0px' }}
        options={filteredCustomers}
        open={open}
        disabled={isDisabled}
        value={innerValue}
        onOpen={onOpenAutocomplete}
        onClose={onCloseAutocomplete}
        onChange={handleChange}
        isOptionEqualToValue={(option, val) => option?.id === val?.id}
        getOptionLabel={customersAutocompleteOptionLabel}
        filterOptions={customersAutocompleteFilterOptions(disableAdd)}
        onInputChange={handleAutocompleteInputChange}
        noOptionsText={isLoading ? 'Loading...' : 'No options'}
        ListboxProps={{
          onScroll: (event: React.SyntheticEvent) => {
            const node = event.currentTarget;
            if (
              Math.ceil(node.scrollTop + node.clientHeight) ===
              node.scrollHeight
            ) {
              onNextPage();
            }
          },
          // @ts-ignore
          ref: listRef,
          onScrollCapture: captureScrollPosition,
        }}
        renderInput={(params) => {
          // params.InputProps.endAdornment is component with 2 children
          // first child is clear button, so we want to set it to null
          const endAdornment = params.InputProps.endAdornment as any;

          return (
            <TextField
              // we are omitting props that would overwrite our styling in TextField
              {..._.omit(params, 'variant', 'size', 'InputLabelProps')}
              className="redesign"
              label={label}
              placeholder={placeholder}
              error={error}
              required={required}
              InputProps={{
                ...params.InputProps,
                ...additionalInputProps,
                endAdornment: (
                  <React.Fragment>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {endAdornment}
                  </React.Fragment>
                ),
              }}
              inputProps={{
                'data-qa': dataQa,
                'data-lpignore': 'true',
                ...params.inputProps,
              }}
            />
          );
        }}
        clearIcon={
          dataQa && (
            <CloseIcon
              fontSize="small"
              data-qa={`${dataQa}-autocomplete-clear-icon`}
            />
          )
        }
        popupIcon={
          dataQa && (
            <ArrowDropDownIcon
              fontSize="small"
              data-qa={`${dataQa}-autocomplete-dropdown-icon`}
            />
          )
        }
      />

      <NewCustomerModal
        show={showModal}
        newCustomerName={newCustomerName}
        onSave={handleSaveModal}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default memo(CustomersAutocomplete);
